import React, { useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { ErrorType } from "@/types";
import {
  RegisterSession
} from "@/types/pos/register"
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import {
  displayDate,
  displayDateTime,
  displayValue,
  formatToPKR,
  sessionMapper,
  downloadCSV,
  formatDate,
} from "@/lib/helper";
import Pagination from "@/components/ui/table/pagination-table";
import usePagination from "@/hooks/use-pagination";
import { DataTableViewOptions } from "./data-table-view-options";
import TableFilters from "@/components/ui/table/data-table-filter";
import { useGetAllRegisterSessionQuery, useLazyGetAllRegisterSessionForExportQuery } from "@/services/registerApi";
import { discrepancy } from "@/lib/constants/cash_register";
import { DatePickerWithRange } from "@/components/ui/date-range/date-rangePicker";
import { Button } from "@/components/ui/button";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import TableSearchFilter from "@/components/ui/table/data-table-search";

interface SearchCriteria {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  search_key?: string;
  start_date?: string;
  end_date?: string;
  type: string;
}

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "cash_register",
};

export default function CashregisterViewTable() {
  const counter_number =
    useSelector((state: RootState) => state.counter?.counter_number) ?? 0;

  const { searchCriteria, query, toggleSortOrder, setSearchCriteria, inputValue, setInputValue } =
    useTableData(initialValue);

  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState<Record<string, any>>({});

  const {
    data: registerSessionData,
    isLoading: isRegisterSessionLoading,
    error,
    isError,
  } = useGetAllRegisterSessionQuery(
    {
      counter_id: counter_number,
      query: query,
    },
    {
      skip: query == "",
    }
  );

  React.useEffect(() => {
    if (isError && navigator.onLine) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: typedError.data?.detail ?? typedError.data?.message,
      });
    }
  }, [isError]);

  const cashregisterTableData = React.useMemo(() => {
    return Array.isArray(registerSessionData?.data)
      ? registerSessionData?.data
      : [];
  }, [registerSessionData]);

  const { toast } = useToast();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [selectAllRows, setSelectAllRows] = useState(false);
  const [showSelectAllNotification, setShowSelectAllNotification] = useState(false);
  const [getAllRegisterSessionForExport] = useLazyGetAllRegisterSessionForExportQuery();

  const handleExportSelected = async () => {
    if (selectAllRows) {
      // Export all rows
      try {
        const filterQuery = Object.keys(filterData).length > 0 
          ? Object.entries(filterData)
              .filter(([_, value]) => value !== "")
              .map(([key, value]) => `${key}=${value}`)
              .join("&")
          : "";
        
        const result = await getAllRegisterSessionForExport({ 
          counter_id: counter_number,
          query: filterQuery 
        }).unwrap();
        
        console.log("result", {result});
        
        if (result?.data && result.data.length > 0) {
          downloadCSV(result.data, "all_cash_register.csv", sessionMapper);
          toast({
            variant: "success",
            title: `Exported ${result.data.length} register sessions successfully`,
          });
        } else {
          toast({
            variant: "destructive",
            title: "No data to export",
            description: "No register session records found to export.",
          });
        }
      } catch (error) {
        console.error("Export failed:", error);
        toast({
          variant: "destructive",
          title: "Export failed",
          description: "Failed to export all register sessions. Please try again.",
        });
      }
    } else {
      // Export selected rows
      const selectedRows = table
        .getSelectedRowModel()
        .rows.map((row) => row.original);
      if (selectedRows.length === 0) {
        toast({
          variant: "destructive",
          title: "Please select one or more records to perform this action.",
        });
        return;
      }
      downloadCSV(selectedRows, "cash_register.csv", sessionMapper);
    }
  };

  const columns: ColumnDef<RegisterSession>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value: any) =>
            table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value: any) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "sessionid",
      meta: "Session Id",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Session ID"
          sortKey="id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <p className="capitalize cursor-pointer">
                <span>{displayValue(row.original.id?.toString())}</span>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "openningtime",
      meta: "Opening Time",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Opening Time"
          sortKey="opening_time"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayDateTime(row?.original.opening_time)}
          </div>
        );
      },
    },
    {
      accessorKey: "openingbalance",
      meta: "Opening Balance",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Opening Balance"
          sortKey="opening_balance"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <p className="capitalize cursor-pointer">
                <span>{formatToPKR(row.original.opening_balance)}</span>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "closingtime",
      meta: "Closing Time",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Closing Time"
          sortKey="closing_time"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayDateTime(row?.original.closing_time)}
          </div>
        );
      },
    },
    {
      accessorKey: "closingbalance",
      meta: "Closing Balance",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Closing Balance"
          sortKey="closing_balance"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <p className="capitalize cursor-pointer">
                <span>{formatToPKR(row.original.closing_balance)}</span>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "discrepancy",
      meta: "Discrepancy",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Discrepancy"
          sortKey="discrepancy"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const Discrepancy = row.original.discrepancy;
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <p className="capitalize cursor-pointer">
                <span
                  // className={`${row.original.discrepancy !== 0 ? "text-red-500" : "text-black"}`}
                  className={`${Discrepancy && (Discrepancy > 0 || Discrepancy < 0)
                    ? "text-red-500"
                    : "text-black"
                    }`}
                >
                  {formatToPKR(row.original.discrepancy)}
                </span>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "notes",
      meta: "Notes",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Notes"
          sortKey="notes"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className=" cursor-pointer">
                      <span>
                        {displayValue(
                          `${row?.original?.notes}`.length > 15
                            ? `${row?.original?.notes}`.substring(0, 15) + "..."
                            : row?.original?.notes
                        )}
                      </span>
                    </p>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p className="text-sm max-w-80">
                      {displayValue(`${row?.original?.notes}`)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "createddate",
      meta: "CreatedDate",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Created Date"
          sortKey="created_date"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayDate(row?.original.created_date)}
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "createdBy",
      meta: "CreatedBy",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Created By"
          sortKey="created_by"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="capitalize cursor-pointer text-nowrap">
                      <span>
                        {displayValue(
                          `${row.original.created_by}`.length > 15
                            ? `${row.original.created_by}`.substring(0, 15) +
                            "..."
                            : `${row.original.created_by}`
                        )}
                      </span>
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="capitalize text-sm">
                      {displayValue(`${row?.original?.created_by}`)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
  ];

  // cashregisterTableData
  const table = useReactTable({
    data: cashregisterTableData as RegisterSession[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  // Handle showing select all notification
  React.useEffect(() => {
    const allPageRowsSelected = table.getIsAllPageRowsSelected();
    const selectedRowCount = table.getSelectedRowModel().rows.length;
    const totalRowsInCurrentPage = table.getRowModel().rows.length;
    
    if (allPageRowsSelected && selectedRowCount === totalRowsInCurrentPage && totalRowsInCurrentPage > 0 && !selectAllRows) {
      setShowSelectAllNotification(true);
    } else if (selectedRowCount === 0 || selectAllRows) {
      setShowSelectAllNotification(false);
    }
  }, [table.getIsAllPageRowsSelected(), table.getSelectedRowModel().rows.length, selectAllRows]);

  // Reset select all when row selection changes
  React.useEffect(() => {
    if (Object.keys(rowSelection).length === 0) {
      setSelectAllRows(false);
      setShowSelectAllNotification(false);
    }
  }, [rowSelection]);

  // Auto-select all visible rows when selectAllRows is true and data changes
  React.useEffect(() => {
    if (selectAllRows && cashregisterTableData.length > 0) {
      table.toggleAllPageRowsSelected(true);
    }
  }, [selectAllRows, cashregisterTableData, table]);

  const totalRecords = registerSessionData?.filtered_counts ?? 0;

  const handleSelectAllRows = () => {
    setSelectAllRows(true);
    setShowSelectAllNotification(false);
  };

  const handleClearSelection = () => {
    setSelectAllRows(false);
    setShowSelectAllNotification(false);
    table.toggleAllPageRowsSelected(false);
    setRowSelection({});
  };

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteria>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });
  const handleDiscrepancy = (value: string) => {
    setFilterData((prev) => ({
      ...prev,
      discrepancy: value,
    }));
  };

  const handleDateRange = (dates: {
    start_date: Date | undefined;
    end_date: Date | undefined;
  }) => {
    const formattedStartDate = dates.start_date
      ? formatDate(dates.start_date)
      : "";
    const formattedEndDate = dates.end_date ? formatDate(dates.end_date) : "";

    setFilterData((prev) => ({
      ...prev,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    }));

    setSearchCriteria((prev: any) => ({
      ...prev,
      start_date: formattedStartDate ?? undefined,
      // Only set end_date if start_date is present
      end_date:
        formattedStartDate && !formattedEndDate ? undefined : formattedEndDate,
      offset: 0,
      sort_key: "id",
      sort_order: "desc",
    }));
  };

  const filterDisplay = [
    {
      type: "select",
      name: "discrepancy",
      label: "Discrepancy",
      options: discrepancy.map((item) => ({
        id: item.value,
        name: item.label,
      })),
      function: handleDiscrepancy,
    },
  ];
  console.log("limit here", searchCriteria.limit, searchCriteria.offset);
  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-3">
        <TableSearchFilter
          placeHolder="Search by Name"
          setSearchCriteria={setSearchCriteria}
          inputValue={inputValue} setInputValue={setInputValue}
        />
        {/* Buttons Container */}

        <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
          <div className="text-sm  text-black flex items-center gap-1  lg:mb-0 h-8 ">
            <DatePickerWithRange
              name={"Date Range"}
              value={{
                start_date: filterData.start_date, // Access start_date directly
                end_date: filterData.end_date, // Access end_date directly
              }}
              onValueChange={(dates) => {
                handleDateRange(dates);
              }}
              label={"Select date range"}
              className="w-full" // Ensure full width
            />
          </div>

          <Button
            variant={"outline"}
            className="text-sm  text-black flex items-center gap-1  lg:mb-0 h-8 px-2"
            onClick={() => {
              setFilterData({})
              setSearchCriteria(initialValue);
              setInputValue("")
            }}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="gap-2 flex justify-center items-center">
                    <i className="fa-solid fa-arrows-rotate"></i>
                    {""}
                    Reset
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reset Filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Button>
          <DataTableViewOptions table={table} action={handleExportSelected} />

          <button
            className="border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center"
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i className="fa fa-filter"></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>

      {/* Gmail-style Select All Notification */}
      {(showSelectAllNotification || selectAllRows) && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mx-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="text-sm text-blue-800">
              {selectAllRows ? (
                <>
                  All <strong>{totalRecords}</strong> register sessions are selected.
                </>
              ) : (
                <>
                  All <strong>{table.getSelectedRowModel().rows.length}</strong> register sessions on this page are selected.
                </>
              )}
            </div>
            {!selectAllRows && (
              <button
                onClick={handleSelectAllRows}
                className="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
              >
                Select all {totalRecords} register sessions
              </button>
            )}
            {selectAllRows && (
              <button
                onClick={handleClearSelection}
                className="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
              >
                Clear selection
              </button>
            )}
          </div>
        </div>
      )}

      <div className="rounded-none border border-border  ">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          {/* <Table className="w-full overflow-x-scroll"> */}
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            {" "}
            {/* //overflow-y-auto scrollbar-thin */}
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isRegisterSessionLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <>
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="h-24">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </>
                ))
              ) : registerSessionData?.total_counts == 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No register closure records found.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No records found for the search Criteria.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* pagination */}
      {cashregisterTableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />
    </div>
  );
}
